import { Field, Label } from '@headlessui/react';
import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  AddContributorIcon,
  CrossIcon,
  EditIcon,
  EyeIconSmall,
  GrantAccessIcon,
  RevokeAccessIcon,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Checkbox } from '../../../components/checkbox/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useFormat } from '../../../hooks/index';
import {
  stakeholderExTypeTitle,
  StakeholderRole,
  TerminationCauseTitle,
} from '../../../types/stakeholder.types';
import { toUpperCaseWords } from '../../../utils/to-uppercase-util.util';
import { getStakeholderCommonActions } from '../stakeholder-permissions.const';
import StakeholderStatus, { StakeholderActionStatus } from '../stakeholder-status';
import { StakeholderTableItemProps } from '../stakeholder-table/stakeholder-table-item';

export const StakeholderTerminatedTableItem: FC<StakeholderTableItemProps> = ({
  stakeholder: {
    role,
    type,
    isActive,
    isAccessRevoked,
    id,
    fullName,
    email,
    isTerminated,
    terminationDate,
    lastVestingDate,
    terminationCause,
    hasShares,
  },
  handleDelete,
  handleEdit,
  handleView,
  handleGrantAccess,
  handleMakeContributor,
  handleRemoveContributorRole,
  handleRevokeAccess,
  handleEditTermination,
  isSelected,
  onRemoveSelect,
  onSelect,
  roleInCompany,
  accountId,
}) => {
  const {
    canDelete,
    grantAccess,
    makeContributor,
    revokeAccess,
    revokeContributor,
    canEditTerminate,
  } = getStakeholderCommonActions({
    role,
    isActive,
    isAccessRevoked,
    isTerminated,
  });

  const { format } = useFormat();

  return (
    <tr className={twMerge('w-full bg-white', isSelected(id) && 'bg-gray-50')} key={id}>
      <td className="relative">
        <div className="flex h-full w-full cursor-pointer items-center justify-center">
          <Field className="flex items-center">
            <Checkbox
              checked={isSelected(id)}
              id={id}
              onChange={(checked) => {
                checked ? onSelect(id) : onRemoveSelect(id);
              }}
            />
            <Label className="absolute left-0 top-0 h-full w-full cursor-pointer" htmlFor={id} />
          </Field>
        </div>
      </td>
      <td className="flex w-fit items-center gap-4 overflow-hidden text-nowrap p-4">
        <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
          {fullName[0]}
          <div className="absolute bottom-0 right-0 flex size-[14px] shrink-0 items-center justify-center rounded-full border-[1px] border-white bg-fireside-600">
            <CrossIcon className="size-[10px]" iconColor="#ffffff" />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip content={fullName}>
            <span className="max-w-[250px] truncate text-sm font-[450] text-gray-700">
              {fullName}
            </span>
          </Tooltip>

          {accountId === id && (
            <span className="text-sm font-[450] text-gray-700">
              {' '}
              (<AppFormattedMessage id="YOU" />)
            </span>
          )}
        </div>
      </td>
      <td className="p-4 text-sm text-gray-700">
        {toUpperCaseWords(stakeholderExTypeTitle[type])}
      </td>
      <td className="overflow-hidden p-4">
        <span className="max-w-[100px] truncate text-sm text-gray-700">
          {terminationCause && <AppFormattedMessage id={TerminationCauseTitle[terminationCause]} />}
        </span>
      </td>
      <td className="p-4 text-sm text-gray-700">{format(terminationDate, 'dd/MM/yyyy')}</td>
      <td className="p-4 text-sm text-gray-700">{format(lastVestingDate, 'dd/MM/yyyy')}</td>
      <td className="p-4 text-sm text-gray-700">{email}</td>
      <td className="p-4 text-sm text-gray-700">
        <div className="flex h-full w-full shrink-0 items-center justify-between gap-4 pr-4">
          <div className="flex gap-4">
            <StakeholderStatus
              type={isActive ? StakeholderActionStatus.ACTIVE : StakeholderActionStatus.INACTIVE}
            />
            {isActive && <StakeholderStatus type={role} />}

            {!isActive && (
              <>
                {isAccessRevoked && (
                  <StakeholderStatus type={StakeholderActionStatus.ACCESS_REVOKED} />
                )}
              </>
            )}
          </div>

          <Popover>
            <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
              <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                <ThreeDotsIcon />
              </div>
            </PopoverTrigger>
            <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
              <PopoverClose>
                <div
                  className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => handleView(id)}
                >
                  <EyeIconSmall />
                  <span className="text-sm font-normal text-gray-700">
                    <AppFormattedMessage id="VIEW_DETAILS" />
                  </span>
                </div>
                {(role !== StakeholderRole.ADMIN || roleInCompany === StakeholderRole.ADMIN) && (
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleEdit(id)}
                  >
                    <EditIcon />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="EDIT_DETAILS" />
                    </span>
                  </div>
                )}
                {grantAccess && (
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleGrantAccess(id)}
                  >
                    <GrantAccessIcon />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="GRANT_ACCESS" />
                    </span>
                  </div>
                )}

                {makeContributor && (
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleMakeContributor(id)}
                  >
                    <AddContributorIcon />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="MAKE_CONTRIBUTOR" />
                    </span>
                  </div>
                )}

                {revokeAccess && accountId !== id && !isAccessRevoked && (
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleRevokeAccess(id)}
                  >
                    <RevokeAccessIcon />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="REVOKE_ACCESS" />
                    </span>
                  </div>
                )}

                {revokeContributor && accountId !== id && (
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleRemoveContributorRole(id)}
                  >
                    <RevokeAccessIcon />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="REVOKE_CONTRIBUTOR_ROLE" />
                    </span>
                  </div>
                )}
                {canEditTerminate && isTerminated && (
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleEditTermination(id)}
                  >
                    <EditIcon />
                    <span className="text-sm font-normal">
                      <AppFormattedMessage id="EDIT_TERMINATION" />
                    </span>
                  </div>
                )}
                {!hasShares && canDelete && accountId !== id && (
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-fireside-500 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleDelete(id)}
                  >
                    <TrashBinIcon iconColor="#F04438" />
                    <span className="text-sm font-normal text-fireside-500">
                      <AppFormattedMessage id="DELETE" />
                    </span>
                  </div>
                )}
              </PopoverClose>
            </PopoverContent>
          </Popover>
        </div>
      </td>
    </tr>
  );
};
